import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Layout } from '@/components/layout';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import {
  Wand2, Loader2, CheckCircle2, XCircle, AlertCircle,
  RefreshCw, ChevronDown, PackageSearch, Pencil, Check, X,
} from 'lucide-react';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

interface ShopifyStore { id: string; nickname: string; storeUrl: string }

interface ProductRow {
  id: string;
  title: string;
  vendor: string;
  tags: string;
  imageUrl: string | null;
  suggestedType: string;
  finalType: string;
  selected: boolean;
  applyStatus?: 'pending' | 'updated' | 'error';
  applyError?: string;
}

type Phase = 'idle' | 'fetching' | 'loaded' | 'suggesting' | 'applying' | 'done';

function StatusBadge({ status }: { status: ProductRow['applyStatus'] }) {
  if (!status) return null;
  if (status === 'updated') return (
    <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
      <CheckCircle2 className="w-3 h-3" /> Applied
    </span>
  );
  if (status === 'error') return (
    <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-red-500/10 text-red-400 border border-red-500/20">
      <XCircle className="w-3 h-3" /> Error
    </span>
  );
  return (
    <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
      <Loader2 className="w-3 h-3 animate-spin" /> Applying…
    </span>
  );
}

export function ProductTypeFixer() {
  const { token } = useAuthStore();
  const { toast } = useToast();

  const [stores, setStores] = useState<ShopifyStore[]>([]);
  const [storesLoaded, setStoresLoaded] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingVal, setEditingVal] = useState('');

  const abortRef = useRef<AbortController | null>(null);
  const requestRef = useRef(0);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadStores = useCallback(async () => {
    if (storesLoaded) return;
    try {
      const r = await fetch(`${BASE}/api/stores`, { headers: authHeaders });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error ?? 'Failed to load stores');
      if (!Array.isArray(data)) throw new Error('Invalid stores response');
      setStores(data);
      setStoresLoaded(true);
    } catch {
      toast({ title: 'Failed to load stores', variant: 'destructive' });
    }
  }, [storesLoaded, token]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const fetchProducts = async () => {
    if (!selectedStoreId) return;
    const requestId = ++requestRef.current;
    const storeId = selectedStoreId;
    setPhase('fetching');
    setRows([]);
    try {
      const r = await fetch(`${BASE}/api/product-type-fixer/stores/${storeId}/products`, {
        headers: authHeaders,
      });
      if (!r.ok) throw new Error((await r.json()).error ?? 'Fetch failed');
      const data = await r.json();
      const loaded: ProductRow[] = (data.products ?? []).map((p: any) => ({
        ...p,
        finalType: '',
        selected: true,
        applyStatus: undefined,
      }));
      if (requestId !== requestRef.current) return;
      setRows(loaded);
      setPhase('loaded');
      if (loaded.length === 0) {
        toast({ title: 'All products have a product type!', description: 'Nothing to fix here.' });
      }
    } catch (err: any) {
      if (requestId === requestRef.current) {
        toast({ title: 'Failed to fetch products', description: err.message, variant: 'destructive' });
        setPhase('idle');
      }
    }
  };

  const suggestAll = async () => {
    if (!selectedStoreId || rows.length === 0) return;
    const requestId = ++requestRef.current;
    const storeId = selectedStoreId;
    setPhase('suggesting');
    setProgress({ completed: 0, total: rows.length });

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const payload = rows.map(r => ({ id: r.id, title: r.title, vendor: r.vendor, tags: r.tags }));
      const resp = await fetch(`${BASE}/api/product-type-fixer/stores/${storeId}/suggest`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ products: payload }),
        signal: ctrl.signal,
      });
      if (!resp.ok || !resp.body) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error ?? 'Suggestion stream failed to start');
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.type === 'suggestion') {
              if (requestId === requestRef.current) {
                setRows(prev => prev.map(r =>
                  r.id === evt.id
                    ? { ...r, suggestedType: evt.suggestedType, finalType: r.finalType || evt.suggestedType }
                    : r
                ));
              }
            } else if (evt.type === 'progress') {
              if (requestId === requestRef.current) setProgress({ completed: evt.completed, total: evt.total });
            } else if (evt.type === 'done') {
              break;
            } else if (evt.type === 'error') {
              if (requestId === requestRef.current) toast({ title: 'AI suggestion error', description: evt.message, variant: 'destructive' });
            }
          } catch { /* ignore malformed lines */ }
        }
      }
      if (!ctrl.signal.aborted && requestId === requestRef.current) setPhase('loaded');
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        if (requestId === requestRef.current) toast({ title: 'Suggestion failed', description: err.message, variant: 'destructive' });
      }
      if (requestId === requestRef.current) setPhase('loaded');
    }
  };

  const applySelected = async () => {
    const toApply = rows.filter(r => r.selected && r.finalType.trim());
    if (toApply.length === 0) {
      toast({ title: 'Nothing to apply', description: 'Select products and ensure each has a product type.', variant: 'destructive' });
      return;
    }
    const requestId = ++requestRef.current;
    const storeId = selectedStoreId;
    setPhase('applying');
    setProgress({ completed: 0, total: toApply.length });

    setRows(prev => prev.map(r =>
      toApply.find(t => t.id === r.id) ? { ...r, applyStatus: 'pending' } : r
    ));

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const updates = toApply.map(r => ({ id: r.id, title: r.title, productType: r.finalType.trim() }));
      const resp = await fetch(`${BASE}/api/product-type-fixer/stores/${storeId}/apply`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
        signal: ctrl.signal,
      });
      if (!resp.ok || !resp.body) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error ?? 'Apply stream failed to start');
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let receivedDone = false;
      let appliedCount = 0;
      let errorCount = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.type === 'progress' && evt.latest) {
              if (requestId !== requestRef.current) continue;
              if (evt.latest.status === 'updated') appliedCount++;
              if (evt.latest.status === 'error') errorCount++;
              setRows(prev => prev.map(r =>
                r.id === evt.latest.id
                  ? { ...r, applyStatus: evt.latest.status, applyError: evt.latest.error }
                  : r
              ));
              setProgress({ completed: evt.completed, total: evt.total });
            } else if (evt.type === 'done') {
              if (requestId !== requestRef.current) continue;
              receivedDone = true;
              toast({
                title: `Applied ${appliedCount} product type${appliedCount !== 1 ? 's' : ''}`,
                description: errorCount > 0 ? `${errorCount} failed — check rows for details.` : 'All updates saved to Shopify.',
              });
            }
          } catch { /* ignore malformed lines */ }
        }
      }

      if (!receivedDone) {
        if (requestId !== requestRef.current) return;
        setRows(prev => prev.map(r =>
          r.applyStatus === 'pending' ? { ...r, applyStatus: 'error', applyError: 'Update status could not be confirmed because the stream ended early.' } : r
        ));
        toast({
          title: 'Update status incomplete',
          description: `${appliedCount} updates were confirmed. Remaining rows could not be confirmed; reload products before retrying.`,
          variant: 'destructive',
        });
        setPhase('loaded');
        return;
      }

      if (requestId === requestRef.current) setPhase('done');
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        if (requestId === requestRef.current) toast({ title: 'Apply failed', description: err.message, variant: 'destructive' });
      }
      if (requestId === requestRef.current) setPhase('loaded');
    }
  };

  const toggleAll = (checked: boolean) => setRows(prev => prev.map(r => ({ ...r, selected: checked })));
  const toggleRow = (id: string) => setRows(prev => prev.map(r => r.id === id ? { ...r, selected: !r.selected } : r));

  const startEdit = (row: ProductRow) => {
    setEditingId(row.id);
    setEditingVal(row.finalType || row.suggestedType || '');
  };
  const commitEdit = (id: string) => {
    const newType = editingVal.trim();
    setRows(prev => {
      const target = prev.find(r => r.id === id);
      const aiSuggestion = target?.suggestedType ?? '';
      return prev.map(r => {
        if (r.id === id) return { ...r, finalType: newType };
        // Propagate override to all rows sharing the same AI suggestion
        if (aiSuggestion && r.suggestedType === aiSuggestion) {
          return { ...r, finalType: newType };
        }
        return r;
      });
    });
    setEditingId(null);
  };
  const cancelEdit = () => setEditingId(null);

  const isBusy = phase === 'fetching' || phase === 'suggesting' || phase === 'applying';
  const selectedStore = stores.find(s => s.id === selectedStoreId);
  const selectedCount = rows.filter(r => r.selected && r.finalType.trim()).length;
  const totalCount = rows.length;
  const suggestedCount = rows.filter(r => r.suggestedType).length;
  const allSelected = rows.length > 0 && rows.every(r => r.selected);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
            <PackageSearch className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold">Fix Missing Product Types</h1>
            <p className="text-sm text-muted-foreground">Find products with no product type, get AI suggestions, and apply in bulk</p>
          </div>
        </div>

        {/* Controls */}
        <div className="glass-panel rounded-2xl p-6 border border-border/50 space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            {/* Store selector */}
            <div className="flex-1 min-w-[240px]">
              <ShopifyTargetStoreSelector
                stores={stores}
                value={selectedStoreId}
                onChange={(id) => {
                  requestRef.current++;
                  abortRef.current?.abort();
                  setSelectedStoreId(id);
                  setRows([]);
                  setPhase('idle');
                }}
                disabled={isBusy}
                label="Target Store"
              />
            </div>

            {/* Fetch button */}
            <button
              onClick={fetchProducts}
              disabled={!selectedStoreId || isBusy}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-muted/50 border border-border text-sm font-medium hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {phase === 'fetching' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              {phase === 'fetching' ? 'Fetching…' : 'Load Products'}
            </button>
          </div>

          {/* Stats bar */}
          {rows.length > 0 && (
            <div className="flex flex-wrap gap-3">
              <span className="px-3 py-1 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                <span className="text-foreground font-semibold">{totalCount}</span> products missing type
              </span>
              <span className="px-3 py-1 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                <span className="text-primary font-semibold">{suggestedCount}</span> AI suggestions ready
              </span>
              <span className="px-3 py-1 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                <span className="text-foreground font-semibold">{selectedCount}</span> selected to apply
              </span>
            </div>
          )}
        </div>

        {/* Action bar — shown when products are loaded */}
        {rows.length > 0 && (
          <div className="flex flex-wrap gap-3 items-center">
            <button
              onClick={suggestAll}
              disabled={isBusy}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary text-sm font-medium hover:bg-primary/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {phase === 'suggesting' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4" />
              )}
              {phase === 'suggesting'
                ? `Suggesting… (${progress.completed}/${progress.total})`
                : suggestedCount > 0 ? 'Re-suggest All with AI' : 'Suggest All with AI'
              }
            </button>

            <button
              onClick={applySelected}
              disabled={isBusy || selectedCount === 0}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium hover:bg-emerald-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {phase === 'applying' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              {phase === 'applying'
                ? `Applying… (${progress.completed}/${progress.total})`
                : `Apply to ${selectedCount} Product${selectedCount !== 1 ? 's' : ''}`
              }
            </button>
          </div>
        )}

        {/* Products table */}
        {rows.length > 0 && (
          <div className="glass-panel rounded-2xl border border-border/50 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50 text-xs text-muted-foreground uppercase tracking-widest">
                  <th className="px-4 py-3 text-left w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={e => toggleAll(e.target.checked)}
                      className="rounded border-border bg-muted/50 accent-primary"
                    />
                  </th>
                  <th className="px-4 py-3 text-left w-12"></th>
                  <th className="px-4 py-3 text-left">Product</th>
                  <th className="px-4 py-3 text-left w-36">Vendor</th>
                  <th className="px-4 py-3 text-left w-56">Product Type</th>
                  <th className="px-4 py-3 text-left w-24">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {rows.map(row => (
                  <tr
                    key={row.id}
                    className={`transition-colors ${row.selected ? 'bg-muted/20' : 'opacity-50'} hover:bg-muted/50`}
                  >
                    {/* Checkbox */}
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={row.selected}
                        onChange={() => toggleRow(row.id)}
                        disabled={isBusy}
                        className="rounded border-border bg-muted/50 accent-primary"
                      />
                    </td>

                    {/* Thumbnail */}
                    <td className="px-2 py-2">
                      {row.imageUrl ? (
                        <img
                          src={row.imageUrl}
                          alt=""
                          className="w-10 h-10 object-cover rounded-lg border border-border"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-muted/50 border border-border flex items-center justify-center">
                          <PackageSearch className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                    </td>

                    {/* Title */}
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground leading-snug line-clamp-2 max-w-xs">{row.title}</p>
                      {row.tags && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-xs">
                          {row.tags.split(',').slice(0, 4).map(t => t.trim()).join(' · ')}
                        </p>
                      )}
                    </td>

                    {/* Vendor */}
                    <td className="px-4 py-3 text-muted-foreground">{row.vendor || '—'}</td>

                    {/* Product Type (editable) */}
                    <td className="px-4 py-3">
                      {editingId === row.id ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            autoFocus
                            value={editingVal}
                            onChange={e => setEditingVal(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') commitEdit(row.id); if (e.key === 'Escape') cancelEdit(); }}
                            className="flex-1 px-2 py-1 rounded-lg bg-muted border border-primary/40 text-sm text-foreground focus:outline-none focus:border-primary"
                            placeholder="e.g. Alloy Wheels"
                          />
                          <button onClick={() => commitEdit(row.id)} className="p-1 rounded-lg hover:bg-emerald-500/20 text-emerald-400 transition-colors">
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={cancelEdit} className="p-1 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEdit(row)}
                          disabled={isBusy}
                          className="group flex items-center gap-2 w-full text-left"
                        >
                          {row.finalType || row.suggestedType ? (
                            <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors
                              ${row.finalType && row.finalType !== row.suggestedType
                                ? 'bg-blue-500/10 border-blue-500/20 text-blue-700 dark:text-blue-300'
                                : 'bg-primary/10 border-primary/20 text-primary'
                              }`}>
                              {row.finalType || row.suggestedType}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">Click to set manually</span>
                          )}
                          {!isBusy && (
                            <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                          )}
                        </button>
                      )}
                      {/* Show AI vs manual label */}
                      {row.finalType && row.suggestedType && row.finalType !== row.suggestedType && (
                        <p className="text-[10px] text-blue-400 mt-0.5 pl-1">Manual override</p>
                      )}
                      {row.suggestedType && (!row.finalType || row.finalType === row.suggestedType) && (
                        <p className="text-[10px] text-primary/60 mt-0.5 pl-1">AI suggestion</p>
                      )}
                    </td>

                    {/* Apply Status */}
                    <td className="px-4 py-3">
                      <StatusBadge status={row.applyStatus} />
                      {row.applyError && (
                        <p className="text-[10px] text-red-400 mt-0.5 max-w-[120px] truncate" title={row.applyError}>
                          {row.applyError}
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty state */}
        {phase === 'idle' && rows.length === 0 && (
          <div className="glass-panel rounded-2xl border border-border/50 p-16 flex flex-col items-center gap-4 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted/50 border border-border flex items-center justify-center">
              <PackageSearch className="w-8 h-8 text-muted-foreground" />
            </div>
            <div>
              <p className="font-medium text-foreground">Select a store and load products</p>
              <p className="text-sm text-muted-foreground mt-1">Products missing a product type will appear here for review</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
